import { DeepPartial } from 'react-hook-form'
import { mergeDeep } from 'remeda'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { AppConfig, getAppConfig } from '../../config/feature-flags'
import {
  ActionsSettings,
  ActionsSettingsSlice,
  initActionsSettingsSlice,
  PersistedActionsSettingsSlice,
  persistActionsSettingsSlice,
  unPersistActionsSettingsSlice,
} from './actions-settings'
import { AnalyticsSlice, initAnalyticsSlice, persistAnalyticsSlice } from './analytics'
import { BannersVisibilitySlice, initBannersVisibilitySlice, persistBannersVisibilitySlice } from './bannersVisibility'
import { ComplianceSlice, initComplianceSlice, PersistedComplianceSlice, persistComplianceSlice } from './compliance'
import { DialogSlice, initDialogSlice } from './dialogs/slice'
import { GovernanceSlice, initGovernanceSlice, persistGovernanceSlice } from './governance'
import {
  initSandboxSlice,
  PersistedSandboxSlice,
  persistSandboxSlice,
  SandboxSlice,
  unPersistSandboxSlice,
} from './sandbox'
import { initVisibilitySlice, persistVisibilitySlice, VisibilitySlice } from './visibility'
import { ZUSTAND_APP_STORE_LOCAL_STORAGE_KEY, ZUSTAND_APP_STORE_LOCAL_STORAGE_VERSION } from '@/config/consts'
import { filterOutUndefinedKeys } from '@/utils/object'
import { Serializable } from '@/utils/types'

export type StoreState = {
  appConfig: AppConfig
} & DialogSlice &
  SandboxSlice &
  ActionsSettingsSlice &
  ComplianceSlice &
  AnalyticsSlice &
  BannersVisibilitySlice &
  VisibilitySlice &
  GovernanceSlice

export type PersistedState = Serializable<
  PersistedSandboxSlice & PersistedActionsSettingsSlice & PersistedComplianceSlice
>

export const storeImplementation = persist<StoreState, [], [], PersistedState>(
  function initializer(...a): StoreState {
    return {
      ...initDialogSlice(...a),
      ...initActionsSettingsSlice(...a),
      ...initComplianceSlice(...a),
      ...initAnalyticsSlice(...a),
      ...initSandboxSlice(...a),
      ...initBannersVisibilitySlice(...a),
      ...initVisibilitySlice(...a),
      ...initGovernanceSlice(...a),
      appConfig: getAppConfig(),
    }
  },
  {
    name: ZUSTAND_APP_STORE_LOCAL_STORAGE_KEY,
    version: ZUSTAND_APP_STORE_LOCAL_STORAGE_VERSION,
    partialize: (state): PersistedState => ({
      ...persistSandboxSlice(state),
      ...persistActionsSettingsSlice(state),
      ...persistComplianceSlice(state),
      ...persistAnalyticsSlice(state),
      ...persistBannersVisibilitySlice(state),
      ...persistVisibilitySlice(state),
      ...persistGovernanceSlice(state),
    }),
    merge: (_persistedState, currentState) => {
      const persistedState = (_persistedState ?? {}) as DeepPartial<PersistedState>

      const processedPersistedState = filterOutUndefinedKeys({
        ...persistedState,
        actionsSettings: unPersistActionsSettingsSlice(persistedState).actionsSettings,
        sandbox: unPersistSandboxSlice(persistedState).sandbox,
      })

      return mergeDeep(currentState, processedPersistedState) as StoreState
    },
  },
)

export const useStore = create<StoreState>()(storeImplementation)

export function useActionsSettings(): ActionsSettings {
  return useStore((state) => state.actionsSettings)
}
